import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sheet from '../../shared/ui/Sheet';
import { PrimaryButton } from '../../shared/ui';
import { formatDateTime } from '../../shared/models';
import { fetchOrder } from '../../shared/services/orderService';
import { TYPE_STYLE } from './NotificationRow';

// Mirrors iOS RestaurantNotificationDetailView as a bottom sheet: type
// icon or broadcast logo, title + date, broadcast hero image, message,
// and a "View Order" button when the notification links to an order.
export default function NotificationDetailSheet({ notification, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showFullImage, setShowFullImage] = useState(false);

  const style = notification ? (TYPE_STYLE[notification.type] || TYPE_STYLE.order_update) : null;
  const isBroadcast = notification?.type === 'promotion' || notification?.data?.broadcast === 'true';

  const openOrder = async () => {
    if (!notification?.orderId || busy) return;
    setBusy(true);
    setError('');
    try {
      const order = await fetchOrder(notification.orderId);
      if (order) {
        onClose();
        navigate(`/business/orders/${order.id}`);
      } else {
        setError('Order not found. It may have been deleted or you may not have permission to view it.');
      }
    } catch {
      setError('Failed to load order details. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!notification} onClose={onClose} title="Notification">
      {notification && style && (
        <div className="px-4 pb-6 space-y-5">
          <div className="flex items-center gap-4">
            {isBroadcast ? (
              <img src="/ChefLogo.jpg" alt="CheF SKN" className="w-14 h-14 rounded-full object-cover shrink-0 bg-white" />
            ) : (
              <span className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${style.bg} ${style.fg}`}>
                <style.Icon size={24} />
              </span>
            )}
            <div>
              <p className="text-xl font-bold text-gray-900">{notification.title || 'Notification'}</p>
              <p className="text-sm text-gray-400">{formatDateTime(notification.createdAt)}</p>
            </div>
          </div>

          <div className="space-y-3">
            {notification.data?.imageURL && (
              <button type="button" onClick={() => setShowFullImage(true)} className="relative block w-full text-left">
                <img src={notification.data.imageURL} alt="" className="w-full h-[220px] object-cover rounded-xl" />
                <span className="absolute bottom-2.5 right-2.5 bg-black/55 text-white text-[12px] font-semibold px-2.5 py-1 rounded-full">
                  Tap to view
                </span>
              </button>
            )}

            <h4 className="font-bold text-gray-900">Details</h4>
            <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-4 leading-relaxed">
              {notification.message}
            </p>

            {notification.orderId && (
              <span className="inline-block text-[11px] font-medium text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5">
                Order ID: {notification.orderId}
              </span>
            )}
          </div>

          {notification.orderId && (
            <PrimaryButton onClick={openOrder} disabled={busy}>
              {busy ? 'Opening…' : 'View Order'}
            </PrimaryButton>
          )}

          {error && <p className="text-xs text-red-500 text-center">{error}</p>}
        </div>
      )}
      {showFullImage && notification?.data?.imageURL && (
        <div
          className="fixed inset-0 z-[60] bg-black flex items-center justify-center"
          onClick={() => setShowFullImage(false)}
        >
          <img src={notification.data.imageURL} alt="" className="max-w-full max-h-full object-contain" />
          <button
            onClick={() => setShowFullImage(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center text-xl"
            aria-label="Close image"
          >
            ×
          </button>
        </div>
      )}
    </Sheet>
  );
}
